<template>
    <div>
        <b-table :items="tableItems" :fields="tableFields" class="mb-0" small bordered>
            <template #cell(name)="{ item, index }">
                <b-input v-model="editingRowData.name" v-if="editingRowIndex === index" autofocus maxlength="50" />
                <template v-else>{{ item.value.name }}</template>
            </template>
            <template #cell(isEligibleForFilling)="{ item, index }">
                <b-check v-model="editingRowData.isEligibleForFilling" v-if="editingRowIndex === index" />
                <b-badge v-else :variant="item.value.isEligibleForFilling ? 'success' : 'danger'">
                    {{ item.value.isEligibleForFilling ? 'Eligible' : 'Ineligible' }}
                </b-badge>
            </template>
            <template #cell(actions)="{ item, index }">
                <div class="d-flex" style="gap: 6px;">
                    <template v-if="editingRowIndex !== index">
                        <b-button variant="link" @click.prevent="startEdit(item.value, index)" title="Edit">
                            <b-icon icon="pencil" variant="primary" />
                        </b-button>
                        <b-button variant="link" @click.prevent="removeItem(index)" title="Remove">
                            <b-icon icon="x-circle-fill" variant="danger" />
                        </b-button>
                    </template>
                    <template v-else>
                        <b-button variant="link" title="Save" @click.prevent="saveEdit(index)"
                            :disabled="!editingRowData.name?.trim()">
                            <b-icon icon="save" variant="primary" />
                        </b-button>
                        <b-button variant="link" title="Cancel" @click.prevent="cancelEdit">
                            <b-icon icon="x-lg" />
                        </b-button>
                    </template>
                </div>
            </template>
        </b-table>

        <b-button size="sm" variant="primary" @click="addItem">
            Add Status
        </b-button>
    </div>
</template>

<script lang="ts">
import { Vue, Prop, Component, Watch } from "vue-property-decorator";
import { InventoryLotStatus } from "@/models/Configuration";

@Component({ name: "InventoryLotStatusesField" })
export default class InventoryLotStatusesField extends Vue {
    @Prop({ default: () => [] }) value!: InventoryLotStatus[];

    editingRowData: InventoryLotStatus = { name: "", isEligibleForFilling: true };
    editingRowIndex: number = -1;
    localValue: InventoryLotStatus[] = [];

    @Watch('value', { immediate: true, deep: true })
    onValueChange(newValue: InventoryLotStatus[]) {
        this.localValue = [...(newValue || [])];
    }

    get tableItems() {
        return this.localValue.map((item, ix) => ({ index: ix, value: item }));
    }

    get tableFields() {
        return [
            { key: 'name', label: 'Status Name' },
            { key: 'isEligibleForFilling', label: 'Eligible for Filling', class: 'eligible-col' },
            { key: 'actions', label: '', class: 'actions' }
        ];
    }

    startEdit(item: InventoryLotStatus, index: number) {
        this.editingRowIndex = index;
        this.editingRowData = { ...item };
    }

    addItem() {
        const cleaned = this.localValue.filter(a => a.name.trim());
        cleaned.push({ name: "", isEligibleForFilling: true });
        this.localValue = cleaned;
        this.startEdit({ name: "", isEligibleForFilling: true }, cleaned.length - 1);
    }

    removeItem(index: number) {
        const array = [...this.localValue];
        array.splice(index, 1);
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
    }

    cancelEdit() {
        const editingValue = this.localValue[this.editingRowIndex]?.name;
        if (!editingValue) {
            this.removeItem(this.editingRowIndex);
        }
        this.editingRowIndex = -1;
    }

    saveEdit(index: number) {
        const array = [...this.localValue];
        if (!this.editingRowData.name?.trim()) {
            array.splice(index, 1);
        } else {
            array.splice(index, 1, { ...this.editingRowData });
        }
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
        this.editingRowIndex = -1;
    }
}
</script>

<style scoped>
::v-deep(thead th),
::v-deep(td) {
    padding: 1px 7px !important;
}

::v-deep(td) {
    height: 38px !important;
}

::v-deep(.actions) {
    width: 90px;
}

::v-deep(.eligible-col) {
    width: 140px;
    text-align: center;
}
</style>
